import { format } from 'date-fns';
import { TargetCostData } from '@/types/target-cost';
import { formatCurrency, formatPercentage } from '@/utils/format';
import { logError } from '@/utils/logger';
import { calculateTargetCostMetrics } from '@/utils/target-cost-calculations';

/**
 * Prepares Target Cost data for CSV export by formatting values and organizing rows.
 * Uses calculateTargetCostMetrics for all derived values.
 */
export function prepareTargetCostCSVData(data: TargetCostData): string[][] {
  if (!data) {
    throw new Error('No data provided for CSV export');
  }

  // Compute metrics using the existing calculation function
  const metrics = calculateTargetCostMetrics(data);

  const rows: string[][] = [
    ['Target Cost Report'],
    ['Generated on:', format(new Date(), 'dd/MM/yyyy HH:mm')],
    [],
    ['Project Details'],
    ['Project Name:', data.details.projectName || 'Not set'],
    ['Client Name:', data.details.clientName || 'Not set'],
    ['Start Date:', data.details.startDate ? format(new Date(data.details.startDate), 'dd/MM/yyyy') : 'Not set'],
    ['End Date:', data.details.endDate ? format(new Date(data.details.endDate), 'dd/MM/yyyy') : 'Not set'],
    ['Reporting Period:', formatReportingPeriod(data)],
    ['Contract Form:', data.details.contractForm || 'Not set'],
    [],
    ['Costs'],
    ['Total of the Prices:', formatCurrency(data.costs.totalPrices ?? 0)],
    ['Final PWDD:', formatCurrency(data.costs.finalPWDD ?? 0)],
    [],
    ['Metrics'],
    ['Pain/Gain Amount:', formatCurrency(metrics.painGainAmount)],
    ['Total Contractor Share:', formatCurrency(metrics.totalContractorShare)],
    ['Contractor Margin:', formatPercentage(metrics.contractorMargin)],
    ['Cost Variance:', formatPercentage(metrics.costVariance)],
    [],
    ['Pain/Gain Share Ranges'],
    ['Range', 'From (%)', 'To (%)', 'Share (%)', 'Amount in Range', "Contractor's Share"],
  ];

  // Add range calculations
  metrics.rangeCalculations.forEach((range) => {
    const rangeLabel = range.type === 'less-than'
      ? 'Less Than'
      : range.type === 'greater-than'
        ? 'Greater Than'
        : `${range.startPercentage}% - ${range.endPercentage}%`;

    const fromValue = range.type === 'less-than' ? '-' : `${range.startPercentage}%`;
    const toValue = range.type === 'greater-than' ? '-' : `${range.endPercentage}%`;

    rows.push([
      rangeLabel,
      fromValue,
      toValue,
      `${range.sharePercentage}%`,
      formatCurrency(range.amountInRange),
      formatCurrency(range.contractorShare),
    ]);
  });

  // Add totals row
  const totalAmountInRange = metrics.rangeCalculations.reduce(
    (sum, range) => sum + range.amountInRange,
    0
  );
  rows.push([
    'TOTAL',
    '',
    '',
    '',
    formatCurrency(totalAmountInRange),
    formatCurrency(metrics.totalContractorShare),
  ]);

  return rows;
}

/**
 * Exports Target Cost data to CSV file
 */
export function exportTargetCostToCSV(data: TargetCostData): void {
  try {
    const csvRows = prepareTargetCostCSVData(data);
    const csvContent = csvRows
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.setAttribute('href', url);
    link.setAttribute('download', generateFilename(data));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    logError('Target Cost CSV Export failed:', error);
    throw new Error('Failed to export CSV file. Please try again.');
  }
}

function formatReportingPeriod(data: TargetCostData): string {
  const start = data.details.reportingPeriodStart;
  const end = data.details.reportingPeriodEnd;

  if (!start || !end) return 'Not set';
  return `${format(new Date(start), 'dd/MM/yyyy')} to ${format(new Date(end), 'dd/MM/yyyy')}`;
}

function generateFilename(data: TargetCostData): string {
  const projectName = data.details.projectName
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '_') || 'project';

  return `${projectName}_target_cost_${format(new Date(), 'yyyy-MM-dd')}.csv`;
}
